import { supabase } from "../config/supabase";

export interface ReuniaoAgendada {
  id?: string;
  empresa_id: string;
  mandato_id: string;
  tipo: "ordinaria" | "extraordinaria";
  numero: number; // Número sequencial (1ª, 2ª, 3ª reunião, etc.)
  data_prevista: string; // Data planejada
  data_realizada?: string | null; // Data real quando ocorreu
  horario_inicio: string; // Ex: "14:00"
  horario_fim?: string | null;
  local: string;
  pauta?: string | null;
  ata_id?: string | null; // Vínculo com cp_atas_digitais
  status: "agendada" | "realizada" | "cancelada" | "remarcada";
  motivo_cancelamento?: string | null;
  criado_por: string;
  created_at?: string;
  updated_at?: string;
}

export interface CronogramaConfig {
  frequencia: "mensal" | "bimestral"; // NR-05: mensal ou bimestral para ME/EPP risco 1 e 2
  dia_preferencial: number; // 1-31
  horario_inicio: string; // Ex: "14:00"
  duracao_horas: number; // Ex: 2
  local_padrao: string;
  considerar_feriados: boolean;
  considerar_fins_semana: boolean;
}

export interface FeriadoNacional {
  date: string;
  name: string;
  type: string;
}

/**
 * Busca feriados nacionais do Brasil para um ano específico
 * Usa API pública: https://brasilapi.com.br/api/feriados/v1/{ano}
 */
async function buscarFeriadosNacionais(
  ano: number,
): Promise<FeriadoNacional[]> {
  try {
    const response = await fetch(
      `https://brasilapi.com.br/api/feriados/v1/${ano}`,
    );
    if (!response.ok) {
      console.error("Erro ao buscar feriados:", response.statusText);
      return [];
    }
    return await response.json();
  } catch (error) {
    console.error("Erro ao buscar feriados:", error);
    return [];
  }
}

/**
 * Verifica se uma data é fim de semana (sábado ou domingo)
 */
function isFimDeSemana(data: Date): boolean {
  const diaSemana = data.getDay();
  return diaSemana === 0 || diaSemana === 6;
}

/**
 * Verifica se uma data é feriado nacional
 */
function isFeriado(data: Date, feriados: FeriadoNacional[]): boolean {
  const dataStr = data.toISOString().split("T")[0];
  return feriados.some((f) => f.date === dataStr);
}

/**
 * Ajusta uma data para o próximo dia útil
 */
function ajustarParaProximoDiaUtil(
  data: Date,
  feriados: FeriadoNacional[],
  considerarFeriados: boolean,
  considerarFinsSemana: boolean,
): Date {
  const dataAjustada = new Date(data);

  let tentativas = 0;
  const maxTentativas = 30; // Evitar loop infinito

  while (tentativas < maxTentativas) {
    const ehFimDeSemana = considerarFinsSemana && isFimDeSemana(dataAjustada);
    const ehFeriado = considerarFeriados && isFeriado(dataAjustada, feriados);

    if (!ehFimDeSemana && !ehFeriado) {
      break;
    }

    dataAjustada.setDate(dataAjustada.getDate() + 1);
    tentativas++;
  }

  return dataAjustada;
}

/**
 * Gera cronograma anual de reuniões ordinárias
 */
export async function gerarCronogramaAnual(
  empresaId: string,
  mandatoId: string,
  dataInicio: string,
  dataFim: string,
  config: CronogramaConfig,
  criadoPor: string,
): Promise<ReuniaoAgendada[]> {
  console.log("🗓️ Gerando cronograma anual de reuniões...");

  const inicio = new Date(dataInicio);
  const fim = new Date(dataFim);
  const anoInicio = inicio.getFullYear();
  const anoFim = fim.getFullYear();

  // Buscar feriados para todos os anos necessários
  const anosParaBuscar = Array.from(
    { length: anoFim - anoInicio + 1 },
    (_, i) => anoInicio + i,
  );
  const feriadosPorAno = await Promise.all(
    anosParaBuscar.map((ano) => buscarFeriadosNacionais(ano)),
  );
  const feriados = feriadosPorAno.flat();

  console.log(`📅 Feriados carregados: ${feriados.length}`);

  const reunioes: ReuniaoAgendada[] = [];
  let numeroReuniao = 1;
  const incrementoMeses = config.frequencia === "mensal" ? 1 : 2;

  // Calcular data da primeira reunião
  let dataAtual = new Date(inicio);
  dataAtual.setDate(config.dia_preferencial);

  // Se o dia preferencial já passou no mês inicial, começar no próximo mês
  if (dataAtual < inicio) {
    dataAtual.setMonth(dataAtual.getMonth() + 1);
  }

  while (dataAtual <= fim) {
    // Ajustar para dia útil se necessário
    const dataAjustada = ajustarParaProximoDiaUtil(
      dataAtual,
      feriados,
      config.considerar_feriados,
      config.considerar_fins_semana,
    );

    // Calcular horário de fim
    const [horaInicio, minutoInicio] = config.horario_inicio
      .split(":")
      .map(Number);
    const dataHoraFim = new Date(dataAjustada);
    dataHoraFim.setHours(horaInicio + config.duracao_horas);
    dataHoraFim.setMinutes(minutoInicio);
    const horarioFim = `${String(dataHoraFim.getHours()).padStart(2, "0")}:${String(dataHoraFim.getMinutes()).padStart(2, "0")}`;

    const reuniao: ReuniaoAgendada = {
      empresa_id: empresaId,
      mandato_id: mandatoId,
      tipo: "ordinaria",
      numero: numeroReuniao,
      data_prevista: dataAjustada.toISOString().split("T")[0],
      horario_inicio: config.horario_inicio,
      horario_fim: horarioFim,
      local: config.local_padrao,
      pauta: `${numeroReuniao}ª Reunião Ordinária - ${config.frequencia === "mensal" ? "Mensal" : "Bimestral"}`,
      status: "agendada",
      criado_por: criadoPor,
    };

    reunioes.push(reuniao);
    numeroReuniao++;

    // Avançar para o próximo mês/bimestre
    dataAtual = new Date(dataAjustada);
    dataAtual.setMonth(dataAtual.getMonth() + incrementoMeses);
  }

  console.log(`✅ Cronograma gerado: ${reunioes.length} reuniões`);
  return reunioes;
}

/**
 * Salva reuniões no banco de dados (cp_reunioes_agendadas)
 * e cria atas automaticamente para cada reunião
 */
export async function salvarReunioes(
  reunioes: ReuniaoAgendada[],
  empresaId: string,
  usuarioId: string,
  criarAtas: boolean = true,
): Promise<void> {
  console.log(`💾 Salvando ${reunioes.length} reuniões no banco...`);

  const { data: reunioesSalvas, error } = await supabase
    .from("cp_reunioes_agendadas")
    .insert(reunioes)
    .select();

  if (error) {
    console.error("Erro ao salvar reuniões:", error);
    throw new Error("Erro ao salvar cronograma de reuniões");
  }

  console.log("✅ Reuniões salvas com sucesso!");

  // Criar atas automaticamente se solicitado
  if (criarAtas && reunioesSalvas && reunioesSalvas.length > 0) {
    try {
      const { ataService } = await import("./ataService");
      await ataService.criarAtasParaReunioes(
        reunioesSalvas,
        empresaId,
        usuarioId,
      );
    } catch (error) {
      console.error("⚠️ Erro ao criar atas automaticamente:", error);
      // Não falha a operação se a criação de atas falhar
    }
  }
}

/**
 * Lista reuniões agendadas de um mandato
 */
export async function listarReunioesPorMandato(
  mandatoId: string,
): Promise<ReuniaoAgendada[]> {
  const { data, error } = await supabase
    .from("cp_reunioes_agendadas")
    .select("*")
    .eq("mandato_id", mandatoId)
    .order("data_prevista", { ascending: true });

  if (error) {
    console.error("Erro ao listar reuniões:", error);
    throw new Error("Erro ao listar reuniões agendadas");
  }

  return data || [];
}

/**
 * Lista reuniões agendadas de uma empresa
 */
export async function listarReunioesPorEmpresa(
  empresaId: string,
): Promise<ReuniaoAgendada[]> {
  const { data, error } = await supabase
    .from("cp_reunioes_agendadas")
    .select("*")
    .eq("empresa_id", empresaId)
    .order("data_prevista", { ascending: true });

  if (error) {
    console.error("Erro ao listar reuniões:", error);
    throw new Error("Erro ao listar reuniões da empresa");
  }

  return data || [];
}

/**
 * Atualiza status da reunião para "realizada" e vincula com ata
 */
export async function marcarReuniaoRealizada(
  reuniaoId: string,
  dataRealizada: string,
  ataId: string,
): Promise<void> {
  const { error } = await supabase
    .from("cp_reunioes_agendadas")
    .update({
      status: "realizada",
      data_realizada: dataRealizada,
      ata_id: ataId,
      updated_at: new Date().toISOString(),
    })
    .eq("id", reuniaoId);

  if (error) {
    console.error("Erro ao marcar reunião como realizada:", error);
    throw new Error("Erro ao atualizar reunião");
  }
}

/**
 * Cancela uma reunião agendada
 */
export async function cancelarReuniao(
  reuniaoId: string,
  motivo: string,
): Promise<void> {
  const { error } = await supabase
    .from("cp_reunioes_agendadas")
    .update({
      status: "cancelada",
      motivo_cancelamento: motivo,
      updated_at: new Date().toISOString(),
    })
    .eq("id", reuniaoId);

  if (error) {
    console.error("Erro ao cancelar reunião:", error);
    throw new Error("Erro ao cancelar reunião");
  }
}

/**
 * Remarca uma reunião para nova data
 */
export async function remarcarReuniao(
  reuniaoId: string,
  novaData: string,
  motivo: string,
): Promise<void> {
  const { error } = await supabase
    .from("cp_reunioes_agendadas")
    .update({
      data_prevista: novaData,
      status: "remarcada",
      motivo_cancelamento: motivo, // Usar campo para registrar motivo da remarcação
      updated_at: new Date().toISOString(),
    })
    .eq("id", reuniaoId);

  if (error) {
    console.error("Erro ao remarcar reunião:", error);
    throw new Error("Erro ao remarcar reunião");
  }
}

/**
 * Cria reunião extraordinária e ata correspondente
 */
export async function criarReuniaoExtraordinaria(
  empresaId: string,
  mandatoId: string,
  data: string,
  horarioInicio: string,
  local: string,
  pauta: string,
  criadoPor: string,
  motivoExtraordinaria?: string,
): Promise<void> {
  // Buscar número da última reunião extraordinária do mandato
  const { data: ultimaExtraordinaria } = await supabase
    .from("cp_reunioes_agendadas")
    .select("numero")
    .eq("mandato_id", mandatoId)
    .eq("tipo", "extraordinaria")
    .order("numero", { ascending: false })
    .limit(1);

  const numero = ultimaExtraordinaria?.[0]?.numero
    ? ultimaExtraordinaria[0].numero + 1
    : 1;

  const reuniao: ReuniaoAgendada = {
    empresa_id: empresaId,
    mandato_id: mandatoId,
    tipo: "extraordinaria",
    numero,
    data_prevista: data,
    horario_inicio: horarioInicio,
    local,
    pauta,
    status: "agendada",
    criado_por: criadoPor,
  };

  const { data: reuniaoSalva, error } = await supabase
    .from("cp_reunioes_agendadas")
    .insert([reuniao])
    .select()
    .single();

  if (error) {
    console.error("Erro ao criar reunião extraordinária:", error);
    throw new Error("Erro ao criar reunião extraordinária");
  }

  // Criar ata automaticamente para a reunião extraordinária
  if (reuniaoSalva) {
    try {
      const { ataService } = await import("./ataService");
      await ataService.criarAtasParaReunioes(
        [
          {
            ...reuniaoSalva,
            motivo_extraordinaria: motivoExtraordinaria || pauta,
          },
        ],
        empresaId,
        criadoPor,
      );
    } catch (error) {
      console.error("⚠️ Erro ao criar ata para reunião extraordinária:", error);
      // Não falha a operação se a criação da ata falhar
    }
  }
}

/**
 * Exclui uma reunião (apenas se ainda não realizada)
 */
export async function excluirReuniao(reuniaoId: string): Promise<void> {
  // Verificar se a reunião já foi realizada
  const { data: reuniao } = await supabase
    .from("cp_reunioes_agendadas")
    .select("status")
    .eq("id", reuniaoId)
    .single();

  if (reuniao?.status === "realizada") {
    throw new Error(
      "Não é possível excluir reunião já realizada. Cancele-a se necessário.",
    );
  }

  const { error } = await supabase
    .from("cp_reunioes_agendadas")
    .delete()
    .eq("id", reuniaoId);

  if (error) {
    console.error("Erro ao excluir reunião:", error);
    throw new Error("Erro ao excluir reunião");
  }
}

/**
 * Obter estatísticas das reuniões de um mandato
 */
export async function obterEstatisticasReunioes(mandatoId: string): Promise<{
  total: number;
  ordinarias: number;
  extraordinarias: number;
  realizadas: number;
  agendadas: number;
  canceladas: number;
  percentualRealizacao: number;
}> {
  const { data: reunioes } = await supabase
    .from("cp_reunioes_agendadas")
    .select("tipo, status")
    .eq("mandato_id", mandatoId);

  if (!reunioes) {
    return {
      total: 0,
      ordinarias: 0,
      extraordinarias: 0,
      realizadas: 0,
      agendadas: 0,
      canceladas: 0,
      percentualRealizacao: 0,
    };
  }

  const total = reunioes.length;
  const ordinarias = reunioes.filter((r) => r.tipo === "ordinaria").length;
  const extraordinarias = reunioes.filter(
    (r) => r.tipo === "extraordinaria",
  ).length;
  const realizadas = reunioes.filter((r) => r.status === "realizada").length;
  const agendadas = reunioes.filter((r) => r.status === "agendada").length;
  const canceladas = reunioes.filter((r) => r.status === "cancelada").length;
  const percentualRealizacao = total > 0 ? (realizadas / total) * 100 : 0;

  return {
    total,
    ordinarias,
    extraordinarias,
    realizadas,
    agendadas,
    canceladas,
    percentualRealizacao,
  };
}

export const reuniaoService = {
  gerarCronogramaAnual,
  salvarReunioes,
  listarReunioesPorMandato,
  listarReunioesPorEmpresa,
  marcarReuniaoRealizada,
  cancelarReuniao,
  remarcarReuniao,
  criarReuniaoExtraordinaria,
  excluirReuniao,
  obterEstatisticasReunioes,
};
